import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { useEffect, useState } from "react";
import {
  candidateList,
  dispositionAddKeepWarm,
  dispositionDraftClosingNote,
  dispositionOpenWorklist,
  dispositionRecord,
  dispositionRecordNoteSent,
} from "@/api/tools";
import { businessLocalToISO } from "@/api/runtime";
import { useRead, useWrite } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import { MISSING, NA, ObjectReadGuard, Banner, Dialog, Empty, Field, Pill, ReasonDialog, Receipt, Skeleton, Table, fmtDate } from "@/ui";
import { CardGrid, RecordCard } from "@/mc";
import { ACTION_KIND_CHIP_LABELS } from "@/views/actions/labels";
import { CLOSURE_LABELS } from "@/views/b9/labels";

// V1-INTERACTIONS §4.17 (Dispositions — `#requisitions/<R>/offer`) and §4.18 (close). The per-candidate offer page
// (`#requisitions/<R>/offer/<cand>`, §4.16) is the design-system page in views/offer, registered by views/b8/register.ts:
// its Reference Check, Compensation and Offer tabs replace the earlier sections. The requisition-level tab below carries
// the OFFERS on the search, listed separately from the disposition worklist (Codex #8 M14).

const dayToISO = (day: string): string => businessLocalToISO(`${day}T00:00`);

/** The stages an application passes through ON the offer track: reaching any of them means an offer file exists, and it
 *  keeps its page after the attempt ends (Codex #8 M10). `furthest_stage` is used, not `stage`, so a declined or reneged
 *  candidate — whose live stage has moved on to a disposition — is still listed. */
const OFFER_TRACK = ["offer_approved", "alternate", "offer", "offer_accepted", "hris_hired", "hired", "declined", "reneged"];

/** The requisition's Offer tab: the offers on the search and the disposition worklist. `hosted` (F-186): under the
 * requisition record's header and tabs, each block a card of its grid. */
export function OfferTab({ rid, hosted = false }: { rid: string; hosted?: boolean | undefined }) {
  const { register } = useViewState();
  const wl = useRead(["worklist", rid], () => dispositionOpenWorklist({ requisition_id: rid }));
  const pipeline = useRead(["candidates", rid], () => candidateList({ requisition_id: rid }), { retry: false });
  useEffect(() => register({ view: "worklist", ids: { requisition: rid }, summary: `worklist ${wl.data?.open_count ?? "?"}` }), [register, rid, wl.data?.open_count]);
  const inval = [["worklist", rid], ["requisition", rid], ["candidates", rid], ["today"]] as const;
  const record = useWrite((i: Parameters<typeof dispositionRecord>[0], k) => dispositionRecord(i, k), inval);
  const sent = useWrite((i: Parameters<typeof dispositionRecordNoteSent>[0], k) => dispositionRecordNoteSent(i, k), inval);
  const warm = useWrite((i: { requisition_id: string; application_id: string; why: string }, k) => dispositionAddKeepWarm(i, k), inval);
  const [dlg, setDlg] = useState<null | { kind: "record"; aid: string; name: string } | { kind: "note"; aid: string; subject: string; body: string } | { kind: "warm"; aid: string }>(null);
  const [code, setCode] = useState("");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [sentOn, setSentOn] = useState("");
  const [sentRef, setSentRef] = useState("");
  if (!wl.data || wl.error) return <ObjectReadGuard query={wl} />;
  const w = wl.data;
  const full = w.projection === "full";
  const canWarm = !!w.can["add_keep_warm"];
  const offers = (pipeline.data?.items ?? []).filter((x) => OFFER_TRACK.includes(x.furthest_stage));
  const warmable = w.keep_warm as { application_id?: unknown; token?: unknown; keep_warm?: unknown }[];
  // ONE READER, ONE PAGE, ONE NAME. The Offers table above names people from the pipeline read; the keep-warm table
  // below is the same reader on the same page and must not rename them "Candidate 1481" from a token. The token stays
  // as the fallback for exactly the case it is for: the pipeline read refused (the count projection) or still in flight.
  const nameOf = new Map((pipeline.data?.items ?? []).map((x) => [x.id, "display_name" in x && typeof x.display_name === "string" ? x.display_name : null] as const));
  const offersBlock = (
    <>
      {pipeline.isLoading && <Skeleton rows={2} />}
      {pipeline.isError && <Banner error={pipeline.error} onRetry={() => void pipeline.refetch()} />}
      {pipeline.data && offers.length === 0 && <Empty heading="No offers yet">No offer has been opened on this search yet.</Empty>}
      {offers.length > 0 && (
        <Table
          label="Offers"
          head={
            <tr>
              <th className="sticky-l">Candidate</th>
              <th>Where it stands</th>
              {pipeline.data?.projection !== "hrbp" && <th>Disposition</th>}
              <th className="sticky-r">Actions</th>
            </tr>
          }
        >
          {offers.map((x) => (
            <tr key={x.id}>
              <td className="sticky-l">{("display_name" in x && typeof x.display_name === "string" ? x.display_name : null) ?? `Candidate ${x.token.slice(-4).toUpperCase()}`}</td>
              <td>
                {x.stage_label}
                {"keep_warm" in x && x.keep_warm ? <Pill tone="info">keep warm</Pill> : null}
              </td>
              {"disposition_status" in x && <td>{x.disposition_status === "recorded" ? "recorded" : x.disposition_status === "obligation_open" ? <Pill tone="warn">owed</Pill> : <NA />}</td>}
              <td className="sticky-r">
                <a className="btn btn--sm" href={`#requisitions/${rid}/offer/${x.id}`}>
                  The offer →
                </a>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
  const worklistBlock = (
    <>
      {/* no "Close — filled": acceptance alone does not fill, and Filled follows the HRIS hire (CR-034) */}
      <Banner error={record.error ?? sent.error ?? warm.error} />
      {/* the COUNT projection (POLICY §8.2, Codex #8 M08): how many and by which code — never a candidate, never a
          carried reason. The Hiring Manager still keeps their own control below. */}
      {!full && (
        <>
          {w.by_code.length === 0 ? (
            <Empty heading="No dispositions yet">No candidate has left the live set yet.</Empty>
          ) : (
            <Table
              label="Dispositions by reason"
              head={
                <tr>
                  <th>Reason</th>
                  <th>How many</th>
                </tr>
              }
            >
              {w.by_code.map((x) => (
                <tr key={String(x.code ?? "none")}>
                  <td>{x.code_label ?? "not yet recorded"}</td>
                  <td>{x.count}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
      {full && w.rows.length === 0 && <Empty heading="No dispositions yet">No candidate has left the live set yet.</Empty>}
      {full && w.rows.length > 0 && (
        <Table
          label="Disposition worklist"
          head={
            <tr>
              <th className="sticky-l">Candidate</th>
              <th>Furthest stage</th>
              <th>Carried reason</th>
              <th>Code</th>
              <th>Date</th>
              <th>Closing note sent</th>
              <th>Done</th>
              <th className="sticky-r">Action</th>
            </tr>
          }
        >
          {w.rows.map((x) => (
            <tr key={x.application_id}>
              <td className="sticky-l">
                <a href={`#requisitions/${rid}/candidates/${x.application_id}`}>{x.candidate}</a>
                {x.keep_warm ? <Pill tone="info">keep warm</Pill> : null}
              </td>
              <td>{x.furthest_stage_label}</td>
              <td className="long">{x.carried_reason ? x.carried_reason.replace(/_/g, " ") : <NA />}</td>
              <td className="long">{x.code_label ?? <NA />}</td>
              <td className="nowrap">{x.effective_at ? fmtDate(x.effective_at) : x.obligation_due_at ? <span className={x.overdue ? "bad" : "muted"}>due {fmtDate(x.obligation_due_at)}</span> : <NA />}</td>
              <td>{x.note_sent && x.note_sent["sent"] ? "yes" : x.automatic ? MISSING : "no"}</td>
              <td>{x.done ? <Pill tone="ok">done</Pill> : x.automatic ? <Pill tone="muted">automatic</Pill> : <Pill tone="warn">open</Pill>}</td>
              <td className="sticky-r">
                <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                  {w.can["record"] && !x.done && !x.automatic && (
                    <button className="btn btn--sm btn--primary" onClick={() => { const carried = w.codes.find((c) => c["slug"] === x.carried_reason)?.["slug"]; setCode(String(carried ?? w.codes[0]?.["slug"] ?? "")); setWhen(""); setNote(""); setDlg({ kind: "record", aid: x.application_id, name: x.candidate }); }}>
                      Disposition
                    </button>
                  )}
                  {w.can["record"] && x.done && !(x.note_sent && x.note_sent["sent"]) && (
                    <button className="btn btn--sm" onClick={() => void dispositionDraftClosingNote({ requisition_id: rid, application_id: x.application_id }).then((n) => { setSentOn(""); setSentRef(""); setDlg({ kind: "note", aid: x.application_id, subject: n.subject, body: n.body }); })}>
                      Draft closing note
                    </button>
                  )}
                  {canWarm && !x.keep_warm && (
                    <button className="btn btn--sm" onClick={() => setDlg({ kind: "warm", aid: x.application_id })}>
                      Flag to keep warm
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {/* §4.17 — the Hiring Manager's own control, from the SERVER's capability and against a token: it used to be
          hidden inside the Recruiter's action column and so was unreachable for them (Codex #8 M14). */}
      {!full && canWarm && warmable.length > 0 && (
        <>
          <h3 className="sec">Keep warm</h3>
          <Table
            label="Keep warm"
            head={
              <tr>
                <th>Candidate</th>
                <th>Flagged</th>
                <th className="sticky-r">Action</th>
              </tr>
            }
          >
            {warmable.map((x) => {
              const aid = String(x["application_id"] ?? "");
              const token = String(x["token"] ?? "");
              return (
                <tr key={aid}>
                  <td>{nameOf.get(aid) ?? `Candidate ${token.slice(-4).toUpperCase()}`}</td>
                  <td>{x["keep_warm"] ? <Pill tone="info">keep warm</Pill> : <NA />}</td>
                  <td className="sticky-r">
                    {!x["keep_warm"] && (
                      <button className="btn btn--sm" onClick={() => setDlg({ kind: "warm", aid })}>
                        Flag to keep warm
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        </>
      )}
    </>
  );
  const dialogs = (
    <>
      <Dialog title={`Disposition — ${dlg?.kind === "record" ? dlg.name : ""}`} open={dlg?.kind === "record"} onClose={() => setDlg(null)}>
        <Field label="Reason code" required id="dp-code">
          <select id="dp-code" value={code} onChange={(e) => setCode(e.target.value)}>
            {w.codes.map((k) => (
              <option key={String(k["slug"])} value={String(k["slug"])}>
                {String(k["label"])}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Effective date" id="dp-when" hint="Defaults to the decision that stopped the candidate, not today">
          <input id="dp-when" type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <Field label="Note" id="dp-note">
          <textarea id="dp-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="acts">
          <button className="btn" onClick={() => setDlg(null)}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={record.isPending || !code} onClick={() => dlg?.kind === "record" && void record.mutateAsync({ requisition_id: rid, application_id: dlg.aid, code, ...(when ? { effective_at: dayToISO(when) } : {}), ...(note.trim() ? { note: note.trim() } : {}) }).then(() => setDlg(null))}>
            Record
          </button>
        </div>
      </Dialog>
      <Dialog title="Closing note — send from the applicant tracking system" open={dlg?.kind === "note"} onClose={() => setDlg(null)}>
        {dlg?.kind === "note" && (
          <>
            <p>
              <b>Subject:</b> {dlg.subject}
            </p>
            <pre className="itinerary">{dlg.body}</pre>
            <p className="muted">Nothing leaves this application. Copy the note into the applicant tracking system, then record that it went.</p>
            <Field label="Sent" id="ns-when" hint="The day it went out; left blank it is today.">
              <input id="ns-when" type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)} />
            </Field>
            <Field label="ATS reference" id="ns-ref" hint="The message or note it went as, so the record can be found again.">
              <input id="ns-ref" value={sentRef} onChange={(e) => setSentRef(e.target.value)} maxLength={120} />
            </Field>
            <div className="acts">
              <button className="btn" onClick={() => setDlg(null)}>
                Cancel
              </button>
              <button className="btn btn--primary" disabled={sent.isPending} onClick={() => void sent.mutateAsync({ requisition_id: rid, application_id: dlg.aid, ...(sentOn ? { sent_at: dayToISO(sentOn) } : {}), ...(sentRef.trim() ? { reference: sentRef.trim() } : {}) }).then(() => setDlg(null))}>
                Record note sent
              </button>
            </div>
          </>
        )}
      </Dialog>
      <ReasonDialog title="Flag to keep warm" label="Why" confirm="Flag" open={dlg?.kind === "warm"} onClose={() => setDlg(null)} busy={warm.isPending} error={warm.error} onConfirm={(why) => dlg?.kind === "warm" && void warm.mutateAsync({ requisition_id: rid, application_id: dlg.aid, why }).then(() => setDlg(null))}>
        <p className="muted">Writes the flag on the application; the keep-warm list comes later.</p>
      </ReasonDialog>
    </>
  );
  if (hosted) {
    // F-186: the requisition record's Offer tab — the offers and the disposition worklist each a card of the record's
    // grid, under its header (whose Actions menu carries Record what already happened); no heading of its own
    return (
      <div className="screen-only">
        <p className="paper-only">Offers and dispositions are live records, read on screen. The application prints saved versions only (V1-INTERACTIONS §5).</p>
        <CardGrid>
          <RecordCard variant="form" span={2} icon="file" title={ACTION_KIND_CHIP_LABELS.offers} sub="Everyone the room decided to make an offer to, and what happened to it." count={pipeline.data ? offers.length : undefined}>
            {offersBlock}
          </RecordCard>
          <RecordCard variant="form" span="all" icon="users" title={CLOSURE_LABELS.dispositionWorklist} sub={<>{w.open_count} open · {w.recorded_count} recorded{w.overdue_count ? ` · ${w.overdue_count} past the ${w.target_days}-day target` : ` · ${w.target_days}-day target`} · {String(w.requisition_status).replace(/_/g, " ")}</>}>
            {worklistBlock}
          </RecordCard>
        </CardGrid>
        {dialogs}
      </div>
    );
  }
  return (
    <div className="screen-only"><RecordAfterFact id={rid} family="requisition" section="offer" />
      <p className="paper-only">Offers and dispositions are live records, read on screen. The application prints saved versions only (V1-INTERACTIONS §5).</p>
      {/* §4.16 — the OFFERS on this search, which is not the disposition worklist: every candidate who reached the offer
          track, live or finished, each on their own page (Codex #8 M14: "All offers" led to a worklist with no offers) */}
      <div className="hrow">
        <div>
          <h2 className="sec">Offers</h2>
          <p className="muted">Everyone the room decided to make an offer to, and what happened to it.</p>
        </div>
      </div>
      {offersBlock}
      <div className="hrow">
        <div>
          <h2 className="sec">Disposition worklist</h2>
          <p className="muted">
            {w.open_count} open · {w.recorded_count} recorded{w.overdue_count ? ` · ${w.overdue_count} past the ${w.target_days}-day target` : ` · ${w.target_days}-day target`} · {String(w.requisition_status).replace(/_/g, " ")}
          </p>
        </div>
      </div>
      {worklistBlock}
      {dialogs}
    </div>
  );
}
